import styles from './CustomSelect.module.scss'
import { useEffect, useRef, useState } from 'react'
import _ from 'lodash'
import { Form, ListGroup } from 'react-bootstrap'
import useOutside from '@/hooks/useOutside'
import useEscapeKey from '@/hooks/useEscapeKey'
import useArrowNavigation from '@/hooks/useArrowNavigation'
import { ArrowKeyDirection } from '@/interfaces'
import useEnterKey from '@/hooks/useEnterKey'

export type CustomSelectOption<T = any> = T & {
  disabled?: boolean
}

interface Props {
  variant?: 'default' | 'whiteOutline'
  placeholder: string
  options: CustomSelectOption[]
  selected?: CustomSelectOption
  disabled?: boolean
  renderOption: (option: CustomSelectOption) => JSX.Element
  renderInputValue: (option: CustomSelectOption) => JSX.Element
  onChange: (option: CustomSelectOption) => void
  preventCloseOnClickOutside?: boolean
}
const CustomSelect = ({
  variant = 'default',
  placeholder,
  options,
  selected: initialSelected,
  disabled = false,
  renderOption,
  renderInputValue,
  onChange,
  preventCloseOnClickOutside = false,
}: Props) => {
  const [open, setOpen] = useState<boolean>(false)
  const [selected, setSelected] = useState<any | undefined>(initialSelected)
  const [focused, setFocused] = useState<any | undefined>(undefined)
  const singleOption = options.length <= 1

  useEffect(() => {
    setSelected(initialSelected)
  }, [initialSelected])

  // state handlers
  const getInputDisplayValue = () => {
    return selected ? renderInputValue(selected) : placeholder
  }
  const toggleOpen = () => {
    if (open) {
      handleClose()
    } else {
      handleOpen()
    }
  }
  const handleSelect = (option: CustomSelectOption) => {
    setSelected(option)
    onChange(option)
    handleClose()
  }
  const handleOpen = () => {
    if (!singleOption) {
      setOpen(true)
    }
  }
  const handleClose = () => {
    if (open) {
      setOpen(false)
      setFocused(undefined)
    }
  }
  const isSelected = (option: CustomSelectOption) => {
    return _.isEqual(JSON.stringify(option), JSON.stringify(selected))
  }

  // a11y hooks
  const wrapperRef = useRef(null)
  useOutside(wrapperRef, () => {
    if (!preventCloseOnClickOutside) {
      handleClose()
    }
  })
  useEscapeKey(() => {
    handleClose()
  })
  useEnterKey(() => {
    if (focused) {
      handleSelect(focused)
    }
  })
  useArrowNavigation((event: KeyboardEvent, direction: ArrowKeyDirection) => {
    if (open) {
      event.preventDefault()
      const index = options.findIndex((option) => option === focused)
      switch (direction) {
        case 'left':
        case 'up':
          if (index > 0) {
            setFocused(options[index - 1])
          }
          if (index == -1) {
            setFocused(options.slice(-1).pop())
          }
          break
        case 'right':
        case 'down':
          if (index + 1 < options.length) {
            setFocused(options[index + 1])
          }
          break
      }
    }
  })

  return (
    <>
      <Form.Group className={`form-group ${styles.root} ${styles[variant]}`} ref={wrapperRef}>
        <div
          className={`${styles.input} ${open && styles.active} ${disabled === true ? styles.disabled : ''} ${singleOption ? styles.noOptions : ''}`}
          onClick={toggleOpen}
        >
          <div className={styles.label}>{getInputDisplayValue()}</div>
        </div>
        {open && (
          <ListGroup className={styles.dropdown} onMouseEnter={() => setFocused(null)}>
            {options.map((option, index: number) => {
              return (
                <ListGroup.Item
                  className={`${styles.option} ${option.disabled ? styles.disabled : ''} ${isSelected(option) ? styles.selected : ''} ${
                    option === focused ? styles.focus : ''
                  }`}
                  disabled={option.disabled}
                  key={index}
                  onClick={() => handleSelect(option)}
                >
                  {renderOption(option)}
                </ListGroup.Item>
              )
            })}
          </ListGroup>
        )}
      </Form.Group>
    </>
  )
}

export default CustomSelect
